import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTenant } from '../contexts/TenantContext';

const Chatbot = ({ apiBase }) => {
  const { tenant } = useTenant();

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState('visitor');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Initialize conversation on mount
    initializeConversation();
  }, []);

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    scrollToBottom();
  }, [messages]);

  const initializeConversation = async () => {
    try {
      const storedSessionId = localStorage.getItem('chatbot_session_id') || generateSessionId();
      localStorage.setItem('chatbot_session_id', storedSessionId);
      setSessionId(storedSessionId);

      const response = await axios.post(`${apiBase}/chatbot/conversation`, {
        session_id: storedSessionId,
        user_type: userType
      });

      setConversationId(response.data.conversation_id);

      // Add welcome message
      if (messages.length === 0) {
        setMessages([{
          type: 'bot',
          text: 'Hello! 👋 I\'m your sales assistant for {tenant.name} - Premium 1 & 2 BHK Residences in Vidya Vihar, Mumbai.\n\n' +
            '\n\n' +
            'I can help you with:\n' +
            '• Project information\n' +
            '• Pricing and payment plans\n' +
            '• Unit availability\n' +
            '• Site visit scheduling\n' +
            '• Cost sheet generation\n' +
            '• Brokerage information\n\n' +
            'What would you like to know?',
          suggestions: ['Project Details', 'Pricing', 'Unit Availability', 'Schedule Visit']
        }]);
      }
    } catch (error) {
      console.error('Error initializing conversation:', error);
    }
  };

  const generateSessionId = () => {
    return 'chat_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (text = null) => {
    const messageText = text || inputMessage.trim();
    if (!messageText) return;

    // Add user message to UI
    const userMessage = {
      type: 'user',
      text: messageText,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const response = await axios.post(`${apiBase}/chatbot/message`, {
        conversation_id: conversationId,
        session_id: sessionId,
        message: messageText
      });

      // Add bot response
      const botMessage = {
        type: 'bot',
        text: response.data.response,
        intent: response.data.intent,
        suggestions: response.data.suggestions || [],
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        type: 'bot',
        text: 'Sorry, I encountered an error. Please try again or contact our sales team directly.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    handleSendMessage(suggestion);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Chatbot Toggle Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
            color: 'white',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.4)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '28px',
            transition: 'transform 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
          title="Chat with Sales Assistant"
        >
          💬
        </button>
      )}

      {/* Chatbot Window */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            width: '400px',
            maxWidth: '90vw',
            height: '600px',
            maxHeight: '80vh',
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 10000,
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <div
            style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              color: 'white',
              padding: '16px 20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderRadius: '16px 16px 0 0'
            }}
          >
            <div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                💬 Sales Assistant
              </h3>
              <p style={{ margin: '4px 0 0 0', fontSize: '12px', opacity: 0.9 }}>
                {tenant.name}
              </p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                color: 'white',
                fontSize: '24px',
                cursor: 'pointer',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>

          {/* Messages Area */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '20px',
              background: '#f8fafc'
            }}
          >
            {messages.map((msg, idx) => (
              <div key={idx} style={{ marginBottom: '16px' }}>
                {msg.type === 'user' ? (
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <div
                      style={{
                        background: '#2563eb',
                        color: 'white',
                        padding: '12px 16px',
                        borderRadius: '16px 16px 4px 16px',
                        maxWidth: '80%',
                        wordWrap: 'break-word',
                        fontSize: '14px',
                        lineHeight: '1.5'
                      }}
                    >
                      {msg.text}
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <div
                      style={{
                        background: 'white',
                        color: '#1f2937',
                        padding: '12px 16px',
                        borderRadius: '16px 16px 16px 4px',
                        maxWidth: '80%',
                        wordWrap: 'break-word',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        whiteSpace: 'pre-line'
                      }}
                    >
                      {msg.text}
                    </div>
                  </div>
                )}

                {/* Suggestions */}
                {msg.suggestions && msg.suggestions.length > 0 && (
                  <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {msg.suggestions.map((suggestion, sidx) => (
                      <button
                        key={sidx}
                        onClick={() => handleSuggestionClick(suggestion)}
                        style={{
                          background: '#e0f2fe',
                          color: '#0369a1',
                          border: '1px solid #bae6fd',
                          padding: '6px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#bae6fd';
                          e.target.style.borderColor = '#7dd3fc';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = '#e0f2fe';
                          e.target.style.borderColor = '#bae6fd';
                        }}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '8px' }}>
                <div style={{
                  background: 'white',
                  padding: '12px 16px',
                  borderRadius: '16px 16px 16px 4px',
                  fontSize: '14px',
                  color: '#6b7280'
                }}>
                  Typing...
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div
            style={{
              padding: '16px',
              background: 'white',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              gap: '8px'
            }}
          >
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              disabled={loading}
              style={{
                flex: 1,
                padding: '12px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '24px',
                fontSize: '14px',
                outline: 'none'
              }}
            />
            <button
              onClick={() => handleSendMessage()}
              disabled={loading || !inputMessage.trim()}
              style={{
                background: loading || !inputMessage.trim() ? '#d1d5db' : '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '44px',
                height: '44px',
                cursor: loading || !inputMessage.trim() ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                transition: 'background 0.2s'
              }}
            >
              ➤
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;


